import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { listRenderLog } from "@/lib/api";

/**
 * Admin-only viewer for the AI render call log. Every image / video
 * render that flows through the backend's provider layer leaves a row
 * here containing the EXACT prompt that shipped to the model, the
 * reference URLs attached, the outcome (ok/error), timing, and the
 * storyboard/scene context that produced the call.
 *
 * Use this when a render came back "wrong" — the row for that render
 * tells you (1) whether refs were actually attached, (2) exactly what
 * the wrapped prompt looked like, and (3) whether the /edits path
 * silently fell back to text-only.
 */
const KIND_OPTIONS = [
  { key: "", label: "All kinds" },
  { key: "image", label: "Scene images" },
  { key: "thumbnail", label: "Thumbnails" },
  { key: "video", label: "Videos" },
];

const STATUS_OPTIONS = [
  { key: "", label: "All" },
  { key: "ok", label: "OK" },
  { key: "error", label: "Errors only" },
];

function fmtMs(ms) {
  if (ms == null) return "—";
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

function fmtBytes(b) {
  if (b == null) return "—";
  if (b < 1024) return `${b}B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)}KB`;
  return `${(b / (1024 * 1024)).toFixed(2)}MB`;
}

function fmtTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: "short", day: "numeric",
      hour: "2-digit", minute: "2-digit", second: "2-digit",
    });
  } catch { return iso; }
}

export default function RenderLog() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [kind, setKind] = useState("");
  const [status, setStatus] = useState("");
  const [storyboardId, setStoryboardId] = useState("");
  const [openId, setOpenId] = useState(null);

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const d = await listRenderLog({
        kind: kind || undefined,
        status: status || undefined,
        storyboard_id: storyboardId.trim() || undefined,
        limit: 200,
      });
      setItems(Array.isArray(d?.items) ? d.items : []);
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || "Failed";
      setError(String(detail));
      toast.error(String(detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [kind, status]);

  const counts = useMemo(() => {
    const c = { total: items.length, ok: 0, err: 0, no_refs: 0 };
    items.forEach((r) => {
      if (r.status === "ok") c.ok += 1;
      else c.err += 1;
      if ((r.ref_count || 0) === 0) c.no_refs += 1;
    });
    return c;
  }, [items]);

  const opened = useMemo(
    () => items.find((r) => r.log_id === openId) || null,
    [items, openId],
  );

  return (
    <div className="p-6 md:p-10 max-w-[1600px] mx-auto" data-testid="render-log-page">
      <div className="mb-6">
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-yellow-400 mb-1">
          // ADMIN · AI RENDER LOG
        </div>
        <h1 className="font-display text-4xl font-black text-white mb-1">
          Every prompt we shipped to a model
        </h1>
        <p className="text-zinc-400 text-sm max-w-3xl">
          Persistent record of every image + video render call. Each row
          contains the exact wrapped prompt sent to the provider, the
          reference URLs attached (or missing), and the outcome. Auto-purges
          after 30 days.
        </p>
      </div>

      <div className="flex flex-wrap gap-3 mb-6 items-end">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Kind</div>
          <div className="flex gap-1">
            {KIND_OPTIONS.map((o) => (
              <button
                key={o.key}
                data-testid={`render-log-kind-${o.key || "all"}`}
                onClick={() => setKind(o.key)}
                className={`font-mono text-[10px] uppercase tracking-wider px-2.5 py-1 rounded-sm border transition-colors ${
                  kind === o.key
                    ? "border-yellow-400 text-yellow-400"
                    : "border-zinc-800 text-zinc-500 hover:text-white hover:border-zinc-700"
                }`}
              >
                {o.label}
              </button>
            ))}
          </div>
        </div>
        <div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Status</div>
          <div className="flex gap-1">
            {STATUS_OPTIONS.map((o) => (
              <button
                key={o.key}
                data-testid={`render-log-status-${o.key || "all"}`}
                onClick={() => setStatus(o.key)}
                className={`font-mono text-[10px] uppercase tracking-wider px-2.5 py-1 rounded-sm border transition-colors ${
                  status === o.key
                    ? "border-yellow-400 text-yellow-400"
                    : "border-zinc-800 text-zinc-500 hover:text-white hover:border-zinc-700"
                }`}
              >
                {o.label}
              </button>
            ))}
          </div>
        </div>
        <div className="flex-1 min-w-[220px]">
          <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Storyboard id filter</div>
          <div className="flex gap-2">
            <input
              data-testid="render-log-storyboard-filter"
              value={storyboardId}
              onChange={(e) => setStoryboardId(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && load()}
              placeholder="stb_xxxxxxx"
              className="flex-1 bg-black border border-zinc-800 rounded-sm px-2.5 py-1 text-sm text-white font-mono placeholder-zinc-600 focus:border-yellow-400/60 outline-none"
            />
            <button
              data-testid="render-log-refresh"
              onClick={load}
              className="font-mono text-[10px] uppercase tracking-widest px-3 py-1 rounded-sm border border-yellow-400/40 text-yellow-300 hover:bg-yellow-400/10"
            >
              ↻ refresh
            </button>
          </div>
        </div>
      </div>

      <div className="flex gap-6 mb-4 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">
        <span data-testid="render-log-total">{counts.total} rows</span>
        <span className="text-emerald-400" data-testid="render-log-ok-count">{counts.ok} ok</span>
        <span className="text-red-400" data-testid="render-log-error-count">{counts.err} errors</span>
        <span className="text-orange-400" data-testid="render-log-noref-count">{counts.no_refs} sent 0 refs</span>
      </div>

      {loading && (
        <div className="font-mono text-sm text-yellow-400" data-testid="render-log-loading">
          [recon] loading render calls…
        </div>
      )}

      {!loading && error && (
        <div className="border border-red-700/50 bg-red-950/30 rounded-sm p-4 text-red-200 text-sm">
          {error}
        </div>
      )}

      {!loading && !error && items.length === 0 && (
        <div
          className="border border-dashed border-zinc-800 rounded-sm p-10 text-center"
          data-testid="render-log-empty"
        >
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-yellow-400 mb-2">
            // NO CALLS LOGGED YET
          </div>
          <p className="text-zinc-400 text-sm max-w-md mx-auto">
            Trigger any image or video render (Storyboard → Regenerate,
            or scene → ▶ preview) and the row will appear here.
          </p>
        </div>
      )}

      {!loading && !error && items.length > 0 && (
        <div className="border border-zinc-800 rounded-sm overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-black/50 text-zinc-300 font-mono text-[10px] uppercase tracking-wider">
              <tr>
                <th className="text-left px-3 py-2">Time</th>
                <th className="text-left px-3 py-2">Kind</th>
                <th className="text-left px-3 py-2">Channel</th>
                <th className="text-left px-3 py-2">Provider</th>
                <th className="text-left px-3 py-2">Status</th>
                <th className="text-right px-3 py-2">Refs</th>
                <th className="text-right px-3 py-2">Duration</th>
                <th className="text-right px-3 py-2">Size</th>
                <th className="text-left px-3 py-2">Context</th>
                <th className="text-left px-3 py-2">Prompt (preview)</th>
              </tr>
            </thead>
            <tbody>
              {items.map((r) => (
                <tr
                  key={r.log_id}
                  onClick={() => setOpenId(r.log_id)}
                  className="border-t border-zinc-900 hover:bg-zinc-900/50 cursor-pointer"
                  data-testid={`render-log-row-${r.log_id}`}
                >
                  <td className="px-3 py-2 whitespace-nowrap font-mono text-zinc-400 text-xs">
                    {fmtTime(r.created_at)}
                  </td>
                  <td className="px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-cyan-300">
                    {r.kind}
                  </td>
                  <td
                    className="px-3 py-2 text-zinc-200 text-xs max-w-[220px] truncate"
                    title={r.context?.channel_handle || ""}
                    data-testid={`render-log-row-channel-${r.log_id}`}
                  >
                    {r.context?.channel_name
                      || (r.context?.channel_handle ? `@${String(r.context.channel_handle).replace(/^@/, "")}` : "—")}
                  </td>
                  <td className="px-3 py-2 font-mono text-xs text-zinc-300">
                    {r.provider}
                  </td>
                  <td className="px-3 py-2">
                    <span
                      className={`font-mono text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded-sm border ${
                        r.status === "ok"
                          ? "border-emerald-400/40 text-emerald-300"
                          : "border-red-400/50 text-red-300"
                      }`}
                    >
                      {r.status}
                    </span>
                  </td>
                  <td
                    className={`px-3 py-2 text-right font-mono tabular-nums ${
                      (r.ref_count || 0) === 0 ? "text-orange-400" : "text-zinc-200"
                    }`}
                  >
                    {r.ref_count ?? 0}
                  </td>
                  <td className="px-3 py-2 text-right font-mono text-xs text-zinc-400">
                    {fmtMs(r.duration_ms)}
                  </td>
                  <td className="px-3 py-2 text-right font-mono text-xs text-zinc-500">
                    {fmtBytes(r.size_bytes)}
                  </td>
                  <td className="px-3 py-2 font-mono text-[11px] text-zinc-500 whitespace-nowrap">
                    {[r.context?.storyboard_id, r.context?.scene_id, r.context?.channel_handle]
                      .filter(Boolean)
                      .join(" · ") || "—"}
                  </td>
                  <td className="px-3 py-2 text-zinc-300 text-xs max-w-[420px] truncate">
                    {(r.prompt || "").slice(0, 120)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {opened && (
        <div
          className="fixed inset-0 z-[60] bg-black/80 backdrop-blur-sm flex items-start justify-center p-6 overflow-y-auto"
          onClick={() => setOpenId(null)}
          data-testid="render-log-detail-overlay"
        >
          <div
            className="bg-zinc-950 border border-yellow-400/40 rounded-sm w-full max-w-5xl my-8 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-6 py-4 border-b border-zinc-800 flex items-start justify-between gap-4">
              <div className="min-w-0 flex-1">
                <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-yellow-400 mb-1">
                  // RENDER CALL · {opened.log_id}
                </div>
                <h2 className="font-display text-xl font-black text-white truncate">
                  {opened.provider} · {opened.kind} · {opened.status}
                </h2>
                {(opened.context?.channel_name || opened.context?.channel_handle) && (
                  <div
                    className="font-mono text-xs text-cyan-300 mt-1 truncate"
                    data-testid="render-log-detail-channel"
                  >
                    channel · {opened.context.channel_name || ""}
                    {opened.context.channel_handle
                      ? ` (@${String(opened.context.channel_handle).replace(/^@/, "")})`
                      : ""}
                  </div>
                )}
                <div className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500 mt-1">
                  {fmtTime(opened.created_at)} · {fmtMs(opened.duration_ms)} · {fmtBytes(opened.size_bytes)} · {opened.ref_count || 0} refs
                </div>
              </div>
              <button
                onClick={() => setOpenId(null)}
                className="font-mono text-zinc-400 hover:text-yellow-400 text-2xl leading-none shrink-0"
                data-testid="render-log-detail-close"
              >
                ×
              </button>
            </div>

            <div className="p-6 space-y-5">
              {opened.error_msg && (
                <div>
                  <div className="font-mono text-[10px] uppercase tracking-widest text-red-400 mb-1">Error</div>
                  <pre
                    className="bg-red-950/30 border border-red-700/50 rounded-sm p-3 text-red-200 text-xs whitespace-pre-wrap"
                    data-testid="render-log-detail-error"
                  >{opened.error_msg}</pre>
                </div>
              )}

              <div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-400 mb-1">
                  Wrapped prompt sent to model ({opened.prompt_len ?? 0} chars)
                </div>
                <pre
                  className="bg-black border border-zinc-800 rounded-sm p-3 text-zinc-100 text-xs whitespace-pre-wrap max-h-[420px] overflow-y-auto"
                  data-testid="render-log-detail-prompt"
                >{opened.prompt || "(empty)"}</pre>
              </div>

              <div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-400 mb-1">
                  Reference URLs attached ({opened.ref_count || 0})
                </div>
                {(opened.ref_urls || []).length === 0 ? (
                  <div className="font-mono text-xs text-orange-400 border border-orange-500/40 rounded-sm px-3 py-2">
                    ⚠ No refs were attached to this call — the model rendered
                    from prompt text only. If this render looks off, this is
                    almost certainly why.
                  </div>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {(opened.ref_urls || []).map((u, i) => (
                      <a
                        key={i}
                        href={u.startsWith("http") ? u : `${window.location.origin}${u}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block border border-zinc-800 rounded-sm overflow-hidden bg-black hover:border-yellow-400/60"
                        title={u}
                        data-testid={`render-log-detail-ref-${i}`}
                      >
                        <img
                          src={u.startsWith("http") ? u : u}
                          alt={`ref-${i}`}
                          className="w-full h-32 object-cover"
                          onError={(e) => { e.currentTarget.style.opacity = "0.3"; }}
                        />
                        <div className="p-1.5 font-mono text-[9px] text-zinc-500 truncate">
                          {u.replace(/^https?:\/\//, "").slice(0, 60)}
                        </div>
                      </a>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-400 mb-1">Context</div>
                <pre
                  className="bg-black border border-zinc-800 rounded-sm p-3 text-zinc-300 text-xs whitespace-pre-wrap"
                  data-testid="render-log-detail-context"
                >{JSON.stringify(opened.context || {}, null, 2)}</pre>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
